import type { BillingFeature } from '../billing/plans';
import type { Role } from '../constants/roles';
import type { RemoteConfigFlags } from './remoteConfigKeys';
import { filterTabsByHouseScope } from '../nav/houseScopedTabs';

export function isMaintenanceActive(
  remoteMaintenance: boolean,
  platformMaintenance = false,
): boolean {
  return remoteMaintenance || platformMaintenance;
}

export function filterRoleTabs(
  role: Role,
  tabs: string[],
  flags: Pick<RemoteConfigFlags, 'rag' | 'auditExport'>,
): string[] {
  return tabs.filter((tab) => {
    if (tab === 'knowledge' && !flags.rag) return false;
    if (tab === 'analytics' && !flags.auditExport) return false;
    return true;
  });
}

/** Tabs the subscription plan has to cover, by feature key. */
const TAB_ENTITLEMENTS: Record<string, BillingFeature> = {
  knowledge: 'knowledge_base',
  analytics: 'analytics_export',
};

/**
 * Drop tabs the plan does not include. `features` is null while the
 * subscription is unresolved, which leaves every tab visible rather than
 * flashing a paywall on a slow network.
 */
export function filterTabsByEntitlements(
  tabs: string[],
  features: readonly BillingFeature[] | null,
): string[] {
  if (!features) return tabs;
  return tabs.filter((tab) => {
    const required = TAB_ENTITLEMENTS[tab];
    return !required || features.includes(required);
  });
}

/** Remote-config, entitlement and house-scope filters applied in menu order. */
export function composeVisibleRoleTabs(
  role: Role,
  tabs: string[],
  flags: Pick<RemoteConfigFlags, 'rag' | 'auditExport'>,
  hasHouseScope: boolean,
  features: readonly BillingFeature[] | null = null,
): string[] {
  const byRemote = filterRoleTabs(role, tabs, flags);
  const byPlan = filterTabsByEntitlements(byRemote, features);
  return filterTabsByHouseScope(role, byPlan, hasHouseScope);
}

export function resolveActiveTab(
  activeTab: string,
  visibleTabs: string[],
  fallback = 'overview',
): string {
  if (visibleTabs.includes(activeTab)) return activeTab;
  return visibleTabs.includes(fallback) ? fallback : (visibleTabs[0] ?? fallback);
}
